/* ─────────────────────────────────────────────────────────────────────────────
   SaleOrderAgentModal.jsx
   Path: @/components/erp/SaleOrderAgentModal.jsx

   Isolated modal for the Sale Order agent. Follows the same isolation pattern
   as SupplierAgentModal: AgentRunModal early-returns to this component, so
   nothing here can affect Invoice, Supplier, Receipt, PR, or Finance agents.

   Wire it up in AgentRunModal.jsx (top of the component, before any hooks):

       import SaleOrderAgentModal from "@/components/erp/SaleOrderAgentModal";
       ...
       export default function AgentRunModal() {
         const { openAgent } = useErp();
         if (SALE_ORDER_KEYS.includes(openAgent)) return <SaleOrderAgentModal />;
         ...
       }

   Deliberately self-contained — it duplicates a few small presentational
   components from AgentRunModal rather than importing them, because those are
   not exported and sharing them would re-couple the two files. See the notes
   at the bottom of this file for what to extract if you'd rather share.
───────────────────────────────────────────────────────────────────────────── */

import React from "react";
import api from "@/lib/api";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  X, Gear, Trash, WarningCircle, Sparkle, CheckCircle,
  PaperPlaneRight, Robot, User as UserIcon, Plus, CaretDown,
} from "@phosphor-icons/react";
import { useErp } from "@/context/ErpContext";
import { LookupField } from "@/components/erp/WizardFields";

/* ── Config ───────────────────────────────────────────────────────────────
   Every backend-facing string lives here. If your agent key differs, change
   it in one place. AGENT_KEY is what gets sent to /erp-agents/<key>/...;
   SALE_ORDER_KEYS is the set of keys AgentRunModal should route here. */

export const AGENT_KEY = "sales-order-booking";
export const SALE_ORDER_KEYS = [
  "sales-order-booking",
  "sales-order-create",
  "sale-order",
  "sales-order",
];

const EP = {
  chat: `/erp-agents/${AGENT_KEY}/chat`,
  manual: `/erp-agents/${AGENT_KEY}/manual`,
  validate: `/erp-agents/${AGENT_KEY}/validate`,
  sessions: `/erp-agents/${AGENT_KEY}/sessions`,
  newSess: `/erp-agents/${AGENT_KEY}/sessions/new`,
  messages: (sid) => `/erp-agents/${AGENT_KEY}/sessions/${sid}/messages`,
  delSess: (sid) => `/erp-agents/${AGENT_KEY}/sessions/${sid}`,
};

const SESSION_STORAGE_KEY = "sale_order_session_id";

const QUICK_ACTIONS = [
  "Create a sales order",
  // "Check order status",
  // "Continue draft",
  // "Copy a previous order",
];

const BRAND = "#0020C2";

/* ── Helpers ─────────────────────────────────────────────────────────────── */

const ts = () =>
  new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

/* Session id survives closing and reopening the modal, so a half-finished
   order resumes instead of starting over and treating the user's next
   message as the customer name. Guarded for SSR and for browsers with
   storage disabled. */
const readStoredSession = () => {
  if (typeof window === "undefined") return null;
  try { return window.sessionStorage.getItem(SESSION_STORAGE_KEY); } catch { return null; }
};
const writeStoredSession = (id) => {
  if (typeof window === "undefined") return;
  try { window.sessionStorage.setItem(SESSION_STORAGE_KEY, id); } catch { /* no-op */ }
};

/* Backend may return status in upper or lower case. */
const normStatus = (s) => (s || "").toLowerCase();

const OK_STATUSES = ["ok", "success", "completed"];
const DONE_STATUSES = ["ok", "success", "completed", "failed"];

/* Turns a response into user-facing text, preferring whatever the backend
   said over our generic fallbacks. */
function replyFor(data, agentLabel) {
  const status = normStatus(data?.status);
  const msg = data?.reply || data?.response || data?.message;
  if (msg) return msg;
  if (OK_STATUSES.includes(status)) return `${agentLabel || "Sales order"} created in Oracle Fusion.`;
  if (["needs_input", "validating_fields"].includes(status)) return "Fill in the required fields to continue.";
  if (["awaiting_confirm", "awaiting_approval"].includes(status)) return "Review the order details and confirm.";
  if (status === "llm_error") return "I couldn't parse that. Try rephrasing.";
  if (status === "oracle_error") return `Oracle returned an error (HTTP ${data?.http_status ?? "unknown"}).`;
  if (status === "unconfigured") return "Your Oracle profile isn't set up. Open settings to configure it.";
  return "Something unexpected came back from the server.";
}

/* Pydantic v2 sends validation errors as an array of objects, which React
   cannot render as children. Flatten to a string. */
function readableError(e) {
  const raw = e?.response?.data?.detail;
  if (Array.isArray(raw)) {
    return raw
      .map((err) => {
        const field = Array.isArray(err.loc) ? err.loc.join(" → ") : String(err.loc || "");
        return field ? `${field}: ${err.msg}` : err.msg;
      })
      .join(" · ");
  }
  if (raw && typeof raw === "object") return JSON.stringify(raw);
  return raw || e?.message || "Unknown error";
}

/* Detects the confirm gate from the status, or from the reply text when the
   agent asks conversationally without flipping status. */
function isConfirmGate(status, reply) {
  if (status === "awaiting_confirm") return true;
  const t = (reply || "").toLowerCase();
  return (
    (t.includes("reply confirm") && t.includes("oracle")) ||
    t.includes("confirm to create") ||
    (t.includes("confirm") && t.includes("create this order")) ||
    (t.includes("confirm") && t.includes("submit this order"))
  );
}

/* ── Markdown renderer for assistant messages ────────────────────────────── */

function ChatMarkdown({ children }) {
  return (
    <div className="chat-md text-sm leading-relaxed">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          p: (p) => <p className="mb-2 last:mb-0 whitespace-pre-wrap" {...p} />,
          strong: (p) => <strong className="font-bold text-gray-900" {...p} />,
          ul: (p) => <ul className="list-disc pl-5 mb-2 space-y-0.5" {...p} />,
          ol: (p) => <ol className="list-decimal pl-5 mb-2 space-y-0.5" {...p} />,
          li: (p) => <li className="text-sm" {...p} />,
          h1: (p) => <h1 className="text-base font-bold mb-1 mt-2" {...p} />,
          h2: (p) => <h2 className="text-sm font-bold mb-1 mt-2" {...p} />,
          h3: (p) => <h3 className="text-sm font-bold mb-1 mt-2 text-[#0020C2]" {...p} />,
          code: ({ inline, ...p }) =>
            inline
              ? <code className="font-mono text-[12px] bg-gray-100 px-1 py-0.5 rounded" {...p} />
              : <code className="font-mono text-[12px] block bg-gray-50 border border-gray-200 rounded p-2 overflow-x-auto" {...p} />,
          table: (p) => (
            <div className="overflow-x-auto my-2">
              <table className="w-full text-xs border-collapse" {...p} />
            </div>
          ),
          thead: (p) => <thead className="bg-[#0020C2]/5" {...p} />,
          th: (p) => <th className="border border-gray-200 px-2 py-1.5 text-left font-bold uppercase tracking-wider text-[10px] text-gray-600" {...p} />,
          td: (p) => <td className="border border-gray-200 px-2 py-1.5 text-gray-800" {...p} />,
          a: (p) => <a className="text-[#0020C2] underline" {...p} />,
          hr: () => <hr className="my-2 border-gray-200" />,
        }}
      >
        {children}
      </ReactMarkdown>
    </div>
  );
}

/* ── Chat-embedded lookup field ───────────────────────────────────────────
   Renders the exact same LookupField the Wizard panel uses — same live,
   debounced GET /erp-agents/lookups/{lookup} search-as-you-type, same
   auth/network/oracle-error banners — just placed inline in a chat bubble
   instead of the wizard grid. next_field_ui only says *which* lookup key
   applies (see backend field_ui.py); this component does the actual
   fetching, exactly like the wizard does.

   IMPORTANT — object identity matters here. LookupField's internal
   extraParams useMemo depends on the raw `allValues` object reference
   (not its contents). Passing a fresh `{}` literal on every render makes
   that memo recompute every render → recreates fetchRows → re-fires the
   fetch effect while the dropdown is open → each successful fetch calls
   setLabelCache (state that lives HERE) → this component re-renders →
   new `{}` literal again → infinite loop. That loop is what showed up as
   the picker endlessly flashing "Searching Oracle…" and never letting a
   click land. EMPTY_OBJECT and the memoized `field` below give LookupField
   stable references, so nothing downstream sees a "change" when nothing
   actually changed.

   IMPORTANT (separate issue): LookupField's onChange gives (value, label).
   `value` is often an Oracle internal id (e.g. BusinessUnitId) — right for
   the wizard's own consumers, wrong here. nodes.py's order_data needs the
   human name ("BusinessUnitName": order_data["business_unit_name"]), so
   this always sends `label` back into chat, falling back to value only if
   a lookup entry has no separate label. */

const EMPTY_OBJECT = Object.freeze({});

function ChatLookupField({ fieldUi, onPick }) {
  const [value, setValue] = React.useState("");
  const [labelCache, setLabelCache] = React.useState({});

  const field = React.useMemo(
    () => ({
      name: fieldUi.field,
      label: fieldUi.label || fieldUi.field.replace(/_/g, " "),
      lookup: fieldUi.lookup,
    }),
    [fieldUi.field, fieldUi.label, fieldUi.lookup]
  );

  const fixedParams = React.useMemo(
    () => fieldUi.extra_params || EMPTY_OBJECT,
    [fieldUi.extra_params]
  );

  return (
    <div className="mt-2" data-testid={`so-field-ui-${fieldUi.field}`}>
      <LookupField
        field={field}
        value={value}
        onChange={(v, label) => {
          setValue(v);
          onPick(label || v);
        }}
        missing={false}
        onCreateNew={undefined}
        lookupSpecs={EMPTY_OBJECT}
        allValues={EMPTY_OBJECT}
        onDependencyChange={undefined}
        labelCache={labelCache}
        setLabelCache={setLabelCache}
        fixedParams={fixedParams}
      />
    </div>
  );
}

/* ── Multi-select ────────────────────────────────────────────────────────── */

function MultiSelectOptions({ fieldUi, onSubmit }) {
  const [selected, setSelected] = React.useState([]);
  const options = fieldUi.options || [];

  const toggle = (value) =>
    setSelected((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
    );

  return (
    <div className="mt-2 border border-[#0020C2]/20 rounded-lg overflow-hidden bg-white"
      data-testid={`so-field-ui-${fieldUi.field}`}>
      <div className="max-h-48 overflow-y-auto divide-y divide-gray-50">
        {options.length === 0 && (
          <div className="px-3 py-2 text-xs text-gray-400">Nothing to choose from yet.</div>
        )}
        {options.map((opt) => {
          const checked = selected.includes(opt.value);
          return (
            <label key={opt.value}
              className="flex items-center gap-2 px-3 py-2 text-xs hover:bg-[#0020C2]/5 cursor-pointer">
              <input
                type="checkbox"
                checked={checked}
                onChange={() => toggle(opt.value)}
                className="accent-[#0020C2] w-3.5 h-3.5"
              />
              <span className={checked ? "text-[#0020C2] font-medium" : "text-gray-700"}>
                {opt.label}
              </span>
            </label>
          );
        })}
      </div>
      <button
        onClick={() => selected.length && onSubmit(selected)}
        disabled={selected.length === 0}
        data-testid="so-multi-select-submit"
        className="w-full text-xs font-bold px-3 py-2 bg-[#0020C2] text-white
          disabled:bg-gray-200 disabled:text-gray-400 transition-colors"
      >
        {selected.length
          ? `Select ${selected.length} item${selected.length > 1 ? "s" : ""}`
          : "Select above"}
      </button>
    </div>
  );
}

/* ── Order lines builder ──────────────────────────────────────────────────
   Sale-order flavour of the invoice LineItemsBuilder: adds UOM and requested
   ship date per line, drops the tax-percent field (Oracle derives sales-order
   tax from the customer's tax profile rather than a typed rate).

   Serializes to the same convention the other agents use:
       ORDER_LINES: [{item, quantity, uom, unit_price, request_date}]        */

const EMPTY_LINE = { item: "", quantity: "", uom: "EA", unit_price: "", request_date: "" };

function OrderLinesBuilder({ fieldUi, onSubmit }) {
  const currency = fieldUi.currency || "AED";
  const uomOptions = fieldUi.uom_options || ["EA", "BOX", "KG", "L", "HR"];
  const [rows, setRows] = React.useState([{ ...EMPTY_LINE }]);

  const updateRow = (i, key, val) =>
    setRows((prev) => prev.map((r, idx) => (idx === i ? { ...r, [key]: val } : r)));
  const addRow = () => setRows((prev) => [...prev, { ...EMPTY_LINE }]);
  const removeRow = (i) =>
    setRows((prev) => (prev.length === 1 ? prev : prev.filter((_, idx) => idx !== i)));

  const total = rows.reduce((sum, r) => {
    const q = parseFloat(r.quantity) || 0;
    const p = parseFloat(r.unit_price) || 0;
    return sum + q * p;
  }, 0);

  const canSubmit = rows.some((r) => r.item.trim() && parseFloat(r.quantity) > 0);

  const submit = () => {
    const clean = rows
      .filter((r) => r.item.trim() && parseFloat(r.quantity) > 0)
      .map((r) => ({
        item: r.item.trim(),
        quantity: parseFloat(r.quantity) || 0,
        uom: r.uom || "EA",
        unit_price: parseFloat(r.unit_price) || 0,
        request_date: r.request_date || null,
      }));
    onSubmit(`ORDER_LINES: ${JSON.stringify(clean)}`);
  };

  return (
    <div className="mt-2 border border-[#0020C2]/20 rounded-xl bg-white p-3 space-y-2"
      data-testid="so-order-lines-builder">
      <div className="text-[10px] font-bold uppercase tracking-wider text-[#0020C2] flex items-center gap-1">
        <Sparkle size={10} weight="fill" /> Order lines
      </div>

      {rows.map((r, i) => (
        <div key={i} className="space-y-1 pb-2 border-b border-gray-100 last:border-0">
          <div className="flex gap-1.5 items-center">
            <span className="text-[10px] font-mono text-gray-400 w-5 shrink-0">
              {String((i + 1) * 10).padStart(2, "0")}
            </span>
            <input
              value={r.item}
              onChange={(e) => updateRow(i, "item", e.target.value)}
              placeholder="Item number or description"
              className="flex-1 px-2 py-1.5 text-xs border border-gray-300 rounded
                focus:outline-none focus:border-[#0020C2]"
            />
            <button
              onClick={() => removeRow(i)}
              disabled={rows.length === 1}
              title="Remove line"
              className="text-[#DC2626] shrink-0 p-1 disabled:opacity-30">
              <Trash size={12} weight="bold" />
            </button>
          </div>

          <div className="flex gap-1.5 items-center pl-6">
            <input
              value={r.quantity}
              onChange={(e) => updateRow(i, "quantity", e.target.value)}
              placeholder="Qty"
              inputMode="decimal"
              className="w-16 px-2 py-1.5 text-xs border border-gray-300 rounded text-right
                focus:outline-none focus:border-[#0020C2]"
            />
            <select
              value={r.uom}
              onChange={(e) => updateRow(i, "uom", e.target.value)}
              className="w-20 px-2 py-1.5 text-xs border border-gray-300 rounded bg-white
                focus:outline-none focus:border-[#0020C2]">
              {uomOptions.map((u) => <option key={u} value={u}>{u}</option>)}
            </select>
            <input
              value={r.unit_price}
              onChange={(e) => updateRow(i, "unit_price", e.target.value)}
              placeholder="Unit price"
              inputMode="decimal"
              className="w-24 px-2 py-1.5 text-xs border border-gray-300 rounded text-right
                focus:outline-none focus:border-[#0020C2]"
            />
            <input
              type="date"
              value={r.request_date}
              onChange={(e) => updateRow(i, "request_date", e.target.value)}
              title="Requested ship date"
              className="flex-1 min-w-0 px-2 py-1.5 text-xs border border-gray-300 rounded
                focus:outline-none focus:border-[#0020C2]"
            />
          </div>
        </div>
      ))}

      <button onClick={addRow}
        className="text-[11px] font-bold text-[#0020C2] hover:underline flex items-center gap-1">
        <Plus size={11} weight="bold" /> Add line
      </button>

      <div className="bg-gray-50 rounded-lg p-2 text-xs">
        <div className="flex justify-between font-bold text-gray-900">
          <span>Order total</span>
          <span className="font-mono">{currency} {total.toFixed(2)}</span>
        </div>
        <div className="text-[10px] text-gray-400 mt-0.5">
          Tax and freight are calculated by Oracle on submission.
        </div>
      </div>

      <button
        onClick={submit}
        disabled={!canSubmit}
        className="w-full bg-[#0020C2] text-white py-2 rounded-lg text-xs font-bold
          uppercase tracking-wider hover:bg-[#001AA3] disabled:opacity-40">
        Add these lines
      </button>
    </div>
  );
}

/* ── Progress + captured fields ──────────────────────────────────────────── */

function ProgressBar({ progress }) {
  if (!progress) return null;
  const pctMatch = String(progress).match(/\((\d+)%\)/);
  const stepMatch = String(progress).match(/(\d+)\/(\d+)/);
  if (!pctMatch || !stepMatch) return null;
  const pct = parseInt(pctMatch[1], 10);
  const [, done, total] = stepMatch;
  return (
    <div className="px-4 pt-3 pb-1" data-testid="so-progress">
      <div className="flex justify-between text-xs text-gray-500 mb-1">
        <span>Step {done} of {total}</span>
        <span>{pct}% complete</span>
      </div>
      <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
        <div className="h-full bg-[#0020C2] transition-all duration-300" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function CapturedFieldsPanel({ fields }) {
  const entries = Object.entries(fields || {}).filter(
    ([, v]) => v !== null && v !== undefined && v !== ""
  );
  if (!entries.length) return null;

  const render = (v) => {
    if (Array.isArray(v)) return `${v.length} line${v.length === 1 ? "" : "s"}`;
    if (typeof v === "object") return JSON.stringify(v).slice(0, 60);
    return String(v);
  };

  return (
    <details className="mx-4 mb-2 border border-gray-100 rounded-lg" data-testid="so-captured-fields">
      <summary className="cursor-pointer text-xs font-bold text-gray-500 px-3 py-2">
        Captured so far ({entries.length})
      </summary>
      <div className="px-3 pb-2 space-y-1">
        {entries.map(([field, value]) => (
          <div key={field} className="flex justify-between gap-3 text-xs border-t border-gray-50 pt-1">
            <span className="text-gray-400 shrink-0">{field.replace(/_/g, " ")}</span>
            <span className="font-mono text-gray-700 text-right break-all">{render(value)}</span>
          </div>
        ))}
      </div>
    </details>
  );
}

function QuickActions({ onPick }) {
  return (
    <div className="flex gap-2 flex-wrap px-4 pb-2" data-testid="so-quick-actions">
      {QUICK_ACTIONS.map((a) => (
        <button key={a} onClick={() => onPick(a)}
          className="text-xs px-3 py-1 border border-gray-200 rounded-full text-gray-600
            hover:border-[#0020C2] hover:text-[#0020C2] transition-colors">
          {a}
        </button>
      ))}
    </div>
  );
}

/* ── Confirm popup ───────────────────────────────────────────────────────
   Prefers structured data (result.order_summary) when the backend sends it;
   falls back to scraping the reply text, the way the invoice popup does. */

const SUMMARY_LABELS = {
  customer: "Customer",
  customer_name: "Customer",
  business_unit: "Business unit",
  order_type: "Order type",
  order_number: "Order number",
  order_date: "Order date",
  transaction_date: "Order date",
  requested_ship_date: "Requested ship date",
  payment_terms: "Payment terms",
  currency: "Currency",
  ship_to: "Ship-to",
  ship_to_site: "Ship-to",
  bill_to: "Bill-to",
  bill_to_site: "Bill-to",
  salesperson: "Salesperson",
  total_amount: "Order total",
};

function scrapeSummary(text) {
  const clean = String(text || "").replace(/\*\*/g, "");
  const grab = (pattern) => {
    const m = clean.match(pattern);
    return m?.[1]?.trim().replace(/^[-:]\s*/, "") || null;
  };
  const out = [];
  const push = (label, value, highlight = false) => {
    if (value) out.push({ label, value, highlight });
  };

  const amt = clean.match(/(?:Order Total|Total Amount|Total)[^\d]*([\d,]+\.?\d*)/i);
  const cur = clean.match(/\b(AED|USD|EUR|GBP|SAR|INR)\b/);
  if (amt) push("Order total", (cur ? cur[0] + " " : "") + amt[1], true);

  push("Customer", grab(/Customer(?:\s*Name)?[:\s]*([^\n]{1,60})/i));
  push("Business unit", grab(/Business Unit[:\s]*([^\n]{1,60})/i));
  push("Order type", grab(/Order Type[:\s]*([^\n]{1,60})/i));
  push("Order date", grab(/(?:Order|Transaction) Date[:\s]*([^\n]{1,60})/i));
  push("Requested ship date", grab(/Requested? Ship Date[:\s]*([^\n]{1,60})/i));
  push("Payment terms", grab(/Payment Terms[:\s]*([^\n]{1,60})/i));
  push("Ship-to", grab(/Ship[- ]?to(?:\s*Site)?[:\s]*([^\n]{1,60})/i));

  const lines = clean.match(/(\d+)\s+(?:order\s+)?lines?\b/i);
  push("Lines", lines?.[1] ? `${lines[1]} line${lines[1] === "1" ? "" : "s"}` : null);

  return out;
}

function buildSummary(structured, text) {
  if (structured && typeof structured === "object") {
    const rows = Object.entries(structured)
      .filter(([, v]) => v !== null && v !== undefined && v !== "")
      .map(([k, v]) => ({
        label: SUMMARY_LABELS[k] || k.replace(/_/g, " "),
        value: Array.isArray(v) ? `${v.length} line${v.length === 1 ? "" : "s"}` : String(v),
        highlight: k === "total_amount",
      }));
    if (rows.length) return rows;
  }
  const scraped = scrapeSummary(text);
  return scraped.length ? scraped : [{ label: "Details", value: "Review the conversation above" }];
}

function OrderConfirmPopup({ text, summary, onConfirm, onCancel }) {
  const fields = buildSummary(summary, text);
  return (
    <div className="fixed inset-0 flex items-center justify-center z-[9999]"
      style={{ background: "rgba(0,0,0,0.65)", backdropFilter: "blur(4px)" }}
      role="dialog" aria-modal="true" aria-label="Review sales order">
      <div className="bg-white rounded-2xl p-6 w-full mx-4 shadow-2xl"
        style={{ maxWidth: 520, border: "1px solid #E5E7EB" }}>

        <div className="flex items-center gap-3 mb-5">
          <div className="w-9 h-9 rounded-full flex items-center justify-center shrink-0"
            style={{ background: BRAND }}>
            <CheckCircle size={18} weight="fill" color="white" />
          </div>
          <div>
            <p className="font-bold text-gray-900 text-sm">Review sales order</p>
            <p className="text-xs text-gray-500">
              Check the details before creating this order in Oracle Fusion.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2.5 mb-5 max-h-[45vh] overflow-y-auto">
          {fields.map((f, i) => (
            <div key={i} style={{
              gridColumn: f.highlight ? "1 / -1" : "auto",
              background: f.highlight ? "rgba(16,185,129,0.07)" : "#F9FAFB",
              border: f.highlight ? "1px solid rgba(16,185,129,0.25)" : "1px solid #E5E7EB",
              borderRadius: 10,
              padding: "9px 12px",
            }}>
              <p style={{
                fontSize: 10, fontWeight: 700, color: "#9CA3AF",
                textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 2,
              }}>{f.label}</p>
              <p style={{
                fontSize: f.highlight ? 15 : 13,
                fontWeight: f.highlight ? 700 : 500,
                color: f.highlight ? "#059669" : "#111827",
                wordBreak: "break-word",
              }}>{f.value}</p>
            </div>
          ))}
        </div>

        <div className="flex gap-3">
          <button onClick={onConfirm}
            data-testid="so-confirm-create"
            className="flex-1 py-2.5 rounded-xl font-bold text-white text-sm transition-all hover:opacity-90"
            style={{ background: "linear-gradient(135deg, #10B981, #059669)" }}>
            Create sales order
          </button>
          <button onClick={onCancel}
            data-testid="so-confirm-cancel"
            className="flex-1 py-2.5 rounded-xl font-semibold text-sm transition-all hover:bg-gray-100"
            style={{ background: "#F9FAFB", border: "1px solid #E5E7EB", color: "#6B7280" }}>
            Keep editing
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Chat bubble ─────────────────────────────────────────────────────────── */

function ChatBubble({ msg, onOptionClick, onMultiSubmit }) {
  const isUser = msg.role === "user";
  const fieldUi = !isUser && !msg.loading ? msg.result?.next_field_ui : null;
  const uiType = fieldUi?.uiType;

  const HANDLED = [
    "searchable_dropdown", "multi_select",
    "order_lines_builder", "line_items_builder",
  ];

  // New shape from field_ui.py: {field, label, kind: "lookup"|"static", ...}
  const kind = fieldUi?.kind;

  return (
    <div className={`flex gap-2.5 ${isUser ? "flex-row-reverse" : "flex-row"}`}>
      <div className={`shrink-0 w-6 h-6 flex items-center justify-center rounded-full text-white
        ${isUser ? "bg-[#0020C2]" : "bg-gray-700"}`}>
        {isUser ? <UserIcon size={11} weight="bold" /> : <Robot size={11} weight="bold" />}
      </div>

      <div className={`max-w-[82%] rounded-2xl px-3.5 py-2 text-sm leading-relaxed
        ${isUser
          ? "bg-[#0020C2] text-white rounded-tr-sm"
          : "bg-white border border-gray-200 text-gray-900 rounded-tl-sm shadow-sm"}`}>

        {msg.loading && (
          <div className="flex items-center gap-1 py-0.5">
            {[0, 150, 300].map((d) => (
              <span key={d} className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce"
                style={{ animationDelay: `${d}ms` }} />
            ))}
          </div>
        )}

        {!msg.loading && msg.text && (
          isUser
            ? <p className="whitespace-pre-wrap">{msg.text}</p>
            : <ChatMarkdown>{msg.text}</ChatMarkdown>
        )}

        {kind === "lookup" && (
          <ChatLookupField fieldUi={fieldUi} onPick={(text) => onOptionClick?.({ value: text })} />
        )}

        {kind === "static" && (fieldUi.static_options || []).length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1.5" data-testid={`so-field-ui-${fieldUi.field}`}>
            {fieldUi.static_options.map((opt) => (
              <button key={opt.value} onClick={() => onOptionClick?.(opt)}
                className="text-xs px-3 py-1.5 border border-[#0020C2]/30 rounded-full text-[#0020C2]
                  bg-[#0020C2]/5 hover:bg-[#0020C2]/10 transition-colors text-left">
                {opt.label}
              </button>
            ))}
          </div>
        )}

        {/* Legacy uiType shapes, kept for anything not yet migrated to kind */}
        {uiType === "searchable_dropdown" && (
          <div className="mt-2 text-xs text-amber-700">
            (legacy searchable_dropdown widget — migrate this field's next_field_ui to the kind-based shape)
          </div>
        )}

        {uiType === "multi_select" && (
          <MultiSelectOptions fieldUi={fieldUi} onSubmit={(values) => onMultiSubmit?.(values)} />
        )}

        {(uiType === "order_lines_builder" || uiType === "line_items_builder") && (
          <OrderLinesBuilder fieldUi={fieldUi} onSubmit={(text) => onOptionClick?.({ value: text })} />
        )}

        {/* Plain option buttons / dropdown fallback */}
        {fieldUi && !HANDLED.includes(uiType) && fieldUi.options?.length > 0 && (
          <div
            className={uiType === "dropdown" ? "mt-2 flex flex-col gap-1" : "mt-2 flex flex-wrap gap-1.5"}
            data-testid={`so-field-ui-${fieldUi.field}`}>
            {fieldUi.options.map((opt) => (
              <button key={opt.value} onClick={() => onOptionClick?.(opt)}
                className="text-xs px-3 py-1.5 border border-[#0020C2]/30 rounded-full text-[#0020C2]
                  bg-[#0020C2]/5 hover:bg-[#0020C2]/10 transition-colors text-left">
                {opt.label}
              </button>
            ))}
          </div>
        )}

        {!msg.loading && msg.result && (
          <div className="mt-2 space-y-2">
            {(() => {
              const s = normStatus(msg.result.status);
              const isOk = OK_STATUSES.includes(s);
              const isErr = ["oracle_error", "llm_error", "failed", "unconfigured"].includes(s);
              if (!isOk && !isErr) return null;
              return (
                <div className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-[10px]
                  font-bold uppercase tracking-wider
                  ${isOk ? "bg-emerald-100 text-emerald-800" : "bg-red-100 text-red-700"}`}>
                  {isOk
                    ? <CheckCircle size={10} weight="fill" />
                    : <WarningCircle size={10} weight="fill" />}
                  {isOk ? "Created" : "Failed"}
                  {msg.result.http_status ? ` · HTTP ${msg.result.http_status}` : ""}
                </div>
              );
            })()}

            {/* Created order — the number is the thing the user came for. */}
            {msg.result.order_number && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">
                <div className="text-[10px] font-bold uppercase tracking-wider text-emerald-700">
                  Sales order
                </div>
                <div className="font-mono text-sm font-bold text-emerald-900">
                  {msg.result.order_number}
                </div>
                {msg.result.order_status && (
                  <div className="text-[11px] text-emerald-700 mt-0.5">
                    Status: {msg.result.order_status}
                  </div>
                )}
              </div>
            )}

            {msg.result.oracle_response && (
              <details>
                <summary className="text-[10px] font-bold uppercase tracking-wider text-gray-500
                  cursor-pointer hover:text-gray-700">
                  Oracle response
                </summary>
                <pre className="text-[10px] font-mono bg-gray-50 border border-gray-200 rounded p-2
                  mt-1 overflow-x-auto max-h-28">
                  {JSON.stringify(msg.result.oracle_response, null, 2).slice(0, 1200)}
                </pre>
              </details>
            )}
          </div>
        )}

        {msg.timestamp && (
          <div className={`text-[9px] mt-1 opacity-40 ${isUser ? "text-right" : "text-left"}`}>
            {msg.timestamp}
          </div>
        )}
      </div>
    </div>
  );
}

/* ── Input bar ───────────────────────────────────────────────────────────
   Top-level on purpose. Defining it inside the parent makes React treat it
   as a new component type every render, remounting the textarea and losing
   focus after each keystroke. */

function InputBar({ inputRef, value, onChange, onKeyDown, onSend, busy, placeholder }) {
  return (
    <div className="border-t border-gray-200 px-4 py-3 bg-white shrink-0">
      <div className="flex items-end gap-2 border border-gray-300 bg-gray-50
        focus-within:border-[#0020C2] focus-within:ring-2 focus-within:ring-blue-100">
        <textarea
          ref={inputRef}
          value={value}
          onChange={onChange}
          onKeyDown={onKeyDown}
          rows={1}
          disabled={busy}
          placeholder={placeholder}
          data-testid="so-prompt"
          className="flex-1 bg-transparent px-3 py-2.5 text-sm resize-none outline-none
            placeholder-gray-400 font-mono disabled:opacity-50 leading-relaxed"
          style={{ minHeight: "2.5rem", maxHeight: "7.5rem" }}
        />
        <button
          onClick={onSend}
          disabled={busy || !value.trim()}
          aria-label="Send message"
          className="m-2 w-8 h-8 bg-[#0020C2] text-white flex items-center justify-center
            hover:bg-[#001AA3] disabled:opacity-40 transition-colors shrink-0">
          {busy
            ? <span className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            : <PaperPlaneRight size={14} weight="fill" />}
        </button>
      </div>
      <span className="text-[10px] text-gray-400 mt-1 block">↵ send · shift+↵ new line</span>
    </div>
  );
}

/* ── Chat history control ────────────────────────────────────────────────── */

function ChatHistoryControl({ currentSessionId, onNewChat, onOpenSession }) {
  const [open, setOpen] = React.useState(false);
  const [sessions, setSessions] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const wrapRef = React.useRef(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(EP.sessions);
      setSessions(Array.isArray(data) ? data : []);
    } catch {
      setSessions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => { if (open) load(); }, [open, load]);

  React.useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  const deleteSession = async (sid, e) => {
    e.stopPropagation();
    if (!window.confirm("Delete this conversation?")) return;
    try {
      await api.delete(EP.delSess(sid));
      load();
    } catch { /* leave the list as-is */ }
  };

  return (
    <div className="flex items-center gap-3" ref={wrapRef}>
      <button onClick={onNewChat}
        data-testid="so-chat-new"
        title="Start a new order"
        className="text-[11px] font-semibold text-gray-500 hover:text-[#0020C2]
          flex items-center gap-1 shrink-0">
        <Plus size={13} weight="bold" /> New order
      </button>

      <div className="relative">
        <button onClick={() => setOpen((v) => !v)}
          data-testid="so-chat-history-toggle"
          className="text-[11px] font-semibold text-gray-500 hover:text-[#0020C2] flex items-center gap-1">
          History
          <CaretDown size={10} weight="bold" className={`transition-transform ${open ? "rotate-180" : ""}`} />
        </button>

        {open && (
          <div className="absolute right-0 z-40 mt-2 w-72 bg-white border border-gray-200
            rounded-lg shadow-lg max-h-80 overflow-y-auto" data-testid="so-chat-history-list">
            {loading && <div className="px-3 py-2 text-xs text-gray-400">Loading…</div>}
            {!loading && sessions.length === 0 && (
              <div className="px-3 py-2 text-xs text-gray-400">
                No previous orders. Start one from the chat.
              </div>
            )}
            {sessions.map((s) => (
              <div key={s.session_id}
                onClick={() => { onOpenSession(s.session_id); setOpen(false); }}
                className={`px-3 py-2 text-xs cursor-pointer border-b border-gray-50 last:border-0
                  hover:bg-[#0020C2]/5 flex items-center gap-2
                  ${s.session_id === currentSessionId ? "bg-[#0020C2]/5" : ""}`}>
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-gray-800 truncate">{s.title || "Untitled order"}</div>
                  <div className="text-[10px] text-gray-400 flex items-center gap-1.5">
                    <span className={`px-1 py-0.5 rounded text-[8px] font-bold uppercase
                      ${s.status === "completed" ? "bg-emerald-100 text-emerald-700"
                        : s.status === "cancelled" ? "bg-gray-200 text-gray-500"
                          : "bg-blue-100 text-blue-700"}`}>
                      {s.status}
                    </span>
                    {s.updated_at ? new Date(s.updated_at).toLocaleString([], {
                      month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
                    }) : ""}
                  </div>
                </div>
                <button onClick={(e) => deleteSession(s.session_id, e)}
                  aria-label="Delete conversation"
                  className="text-[#DC2626] shrink-0 p-1">
                  <Trash size={11} weight="bold" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/* ═════════════════════════════════════════════════════════════════════════
   MAIN
   ═════════════════════════════════════════════════════════════════════════ */

export default function SaleOrderAgentModal() {
  const { openAgent, setOpenAgent, setSetupOpen, profile, agents } = useErp();

  /* ── State ── */
  const [messages, setMessages] = React.useState([]);
  const [input, setInput] = React.useState("");
  const [busy, setBusy] = React.useState(false);

  const [sessionId, setSessionId] = React.useState(() => readStoredSession() || crypto.randomUUID());

  const [progress, setProgress] = React.useState(null);
  const [capturedFields, setCapturedFields] = React.useState(null);

  const [showConfirm, setShowConfirm] = React.useState(false);
  const [confirmText, setConfirmText] = React.useState("");
  const [confirmSummary, setConfirmSummary] = React.useState(null);

  const agent = agents?.find((a) => a.key === openAgent);
  const agentLabel = agent?.label || "Sales Order";
  const messagesEndRef = React.useRef(null);
  const inputRef = React.useRef(null);

  React.useEffect(() => { writeStoredSession(sessionId); }, [sessionId]);

  React.useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  if (!openAgent) return null;

  /* ── Close ── */
  const close = () => {
    setOpenAgent(null);
    setMessages([]);
    // sessionId is intentionally preserved so reopening resumes the same
    // order rather than starting from scratch.
  };

  /* ── Chat ── */
  const sendMessage = async (text) => {
    const userText = (text || "").trim();
    if (!userText || busy) return;

    setInput("");
    if (inputRef.current) inputRef.current.style.height = "auto";

    const now = ts();
    setMessages((prev) => [
      ...prev,
      { id: Date.now(), role: "user", text: userText, timestamp: now },
      { id: Date.now() + 1, role: "assistant", loading: true },
    ]);
    setBusy(true);

    try {
      const { data } = await api.post(EP.chat, {
        session_id: sessionId,
        message: userText,
      });

      if (data?.progress !== undefined) setProgress(data.progress);
      if (data?.fields_collected !== undefined) setCapturedFields(data.fields_collected);

      const status = normStatus(data?.status);
      const reply = replyFor(data, agentLabel);

      setMessages((prev) =>
        prev.map((m) => (m.loading
          ? { ...m, loading: false, text: reply, result: data, timestamp: ts() }
          : m))
      );

      if (isConfirmGate(status, reply) && !DONE_STATUSES.includes(status)) {
        setConfirmText(reply);
        setConfirmSummary(data?.order_summary || data?.fields_collected || null);
        setShowConfirm(true);
      }
    } catch (e) {
      const detail = readableError(e);
      setMessages((prev) =>
        prev.map((m) => (m.loading
          ? { ...m, loading: false, timestamp: ts(), text: `Couldn't reach the agent: ${detail}` }
          : m))
      );
    } finally {
      setBusy(false);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  };

  /* ── Confirm handlers ── */
  const handleConfirm = () => {
    setShowConfirm(false);
    sendMessage("confirm");
  };

  const handleCancelConfirm = () => {
    setShowConfirm(false);
    setMessages((prev) => [...prev, {
      id: Date.now(),
      role: "assistant",
      timestamp: ts(),
      text: "Order not submitted. Tell me what to change and we'll pick up from here.",
    }]);
  };

  /* ── Input handlers ── */
  const handleInputChange = (e) => {
    setInput(e.target.value);
    e.target.style.height = "auto";
    e.target.style.height = Math.min(e.target.scrollHeight, 120) + "px";
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(input); }
  };

  /* ── Sessions ── */
  const startNewChat = async () => {
    let newId = crypto.randomUUID();
    try {
      const { data } = await api.post(EP.newSess);
      if (data?.session_id) newId = data.session_id;
    } catch { /* fall back to the local uuid */ }
    setSessionId(newId);
    setMessages([]);
    setInput("");
    setShowConfirm(false);
    setConfirmText("");
    setConfirmSummary(null);
    setProgress(null);
    setCapturedFields(null);
  };

  const openSession = async (sid) => {
    try {
      const { data } = await api.get(EP.messages(sid));
      const loaded = (data?.messages || []).map((m, i) => ({
        id: Date.now() + i,
        role: m.role,
        text: m.content,
        timestamp: m.created_at
          ? new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
          : ts(),
        result: m.next_field_ui ? { next_field_ui: m.next_field_ui } : undefined,
      }));
      setSessionId(sid);
      setMessages(loaded);
      setShowConfirm(false);
    } catch (e) {
      window.alert(`Couldn't open that order: ${readableError(e)}`);
    }
  };

  return (
    <>
      <style>{`
        @keyframes soChatFadeIn { from { opacity:0; transform:translateY(6px); } to { opacity:1; transform:translateY(0); } }
        .so-modal-chat {
  width:min(72vw,960px); height:86vh;
  resize: both; overflow: auto;
  min-width: 480px; min-height: 520px;
  max-width: 96vw;  max-height: 96vh;
}
        .so-modal-chat::-webkit-resizer { display: none; }
        .so-resize-hint {
          position: absolute; right: 3px; bottom: 3px; width: 14px; height: 14px;
          cursor: nwse-resize; pointer-events: none; opacity: 0.5;
        }
        @media (max-width: 700px) {
          .so-modal-chat { width: 100vw; min-width: 0; height: 92vh; resize: none; }
        }
        @media (prefers-reduced-motion: reduce) {
          .so-anim { animation: none !important; }
        }
      `}</style>

      <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
        onClick={() =>{if(messages.length === 0) close();}}>
        <div
          onClick={(e) => e.stopPropagation()}
          data-testid={`agent-modal-${openAgent}`}
          className="relative bg-white text-gray-900 border border-gray-300 flex flex-col so-modal-chat so-anim"
          style={{ animation: "soChatFadeIn 0.3s cubic-bezier(0.4,0,0.2,1)" }}>

          <svg className="so-resize-hint" viewBox="0 0 14 14" fill="none" aria-hidden="true">
            <path d="M13 5 L5 13 M13 9 L9 13 M13 1 L1 13" stroke="#9CA3AF" strokeWidth="1.2" />
          </svg>

          {/* ── Header ── */}
          <div className="px-5 py-4 border-b border-gray-200 flex items-start justify-between shrink-0">
            <div>
              <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-gray-500">
                Oracle Fusion ERP · Vaku AI
              </div>
              <h3 className="text-2xl font-black tracking-tight mt-0.5">{agentLabel}</h3>
              <p className="text-xs text-gray-500 mt-0.5">
                {agent?.hint || "Describe the order and I'll build it with you."}
              </p>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              <ChatHistoryControl
                currentSessionId={sessionId}
                onNewChat={startNewChat}
                onOpenSession={openSession}
              />
              <div className="w-px h-5 bg-gray-200" />
              <button
                onClick={() => { close(); setSetupOpen(true); }}
                data-testid="so-open-profile"
                className="text-[11px] font-semibold text-gray-500 hover:text-[#0020C2] flex items-center gap-1">
                <Gear size={13} weight="bold" /> {profile?.oracle_username || "Set up profile"}
              </button>
              <button onClick={close} data-testid="agent-modal-close" aria-label="Close">
                <X size={18} />
              </button>
            </div>
          </div>

          {/* ── Body — chat only, no wizard panel ── */}
          <ProgressBar progress={progress} />
          <CapturedFieldsPanel fields={capturedFields} />

          <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-gray-50">
            {messages.map((msg) => (
              <ChatBubble
                key={msg.id}
                msg={msg}
                onOptionClick={(opt) => sendMessage(opt.value)}
                onMultiSubmit={(values) => sendMessage(values.join(", "))}
              />
            ))}
            <div ref={messagesEndRef} />
          </div>

          {messages.length <= 1 && <QuickActions onPick={(t) => sendMessage(t)} />}

          <InputBar
            inputRef={inputRef}
            value={input}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onSend={() => sendMessage(input)}
            busy={busy}
            placeholder={agent?.hint || "e.g. 10 units of AS54888 for Acme Corp, ship next Friday"}
          />
        </div>
      </div>

      {showConfirm && (
        <OrderConfirmPopup
          text={confirmText}
          summary={confirmSummary}
          onConfirm={handleConfirm}
          onCancel={handleCancelConfirm}
        />
      )}
    </>
  );
}

/* ─────────────────────────────────────────────────────────────────────────
   NOTES

   Chat-only by design — the wizard grid (WizardFields) was intentionally
   dropped from this modal. The LangGraph agent behind /chat now collects
   every required field, including line items, through the conversation
   itself (with live Oracle-backed dropdowns via next_field_ui), so a
   parallel form asking the same questions was redundant surface area to
   keep in sync. EP.manual and EP.validate are left defined below in case
   a future non-chat entry point needs them, but nothing in this component
   calls them anymore.

   Backend contract this file assumes (all under /erp-agents/sales-order-booking):
     POST /chat              { session_id, message }
                             → { status, reply, progress?,
                                 fields_collected?, next_field_ui?, missing?,
                                 order_summary?, order_number?, order_status?,
                                 oracle_response?, http_status? }
     GET  /sessions
     POST /sessions/new      → { session_id }
     GET  /sessions/:id/messages
     DELETE /sessions/:id

   next_field_ui shape (see backend field_ui.py):
     {"field", "label", "kind": "lookup", "lookup": <LOOKUP_CATALOG key>}
     {"field", "label", "kind": "static", "static_options": [...]}
     null → plain text input, no widget

   ChatLookupField reuses WizardFields.jsx's exported LookupField component
   for the live, debounced Oracle search inside chat bubbles — that's the
   one piece still shared with the wizard file, intentionally, so there's
   only one implementation of "search Oracle as you type" in the codebase.
───────────────────────────────────────────────────────────────────────────── */
